import L from "leaflet";
import { MARKER_SLIDE_MS, slideEase, type ModelDevicePoint } from "@safers/model/devices";
import { createDevicePin, updateDevicePin, type DevicePin } from "@safers/model/pin";
import { SITE_Z } from "./zoomFade";

const DEVICE_ICON_SIZE = 32;
const DEVICE_LABEL_ZOOM = SITE_Z + 1;

interface DeviceMarker {
  marker: L.Marker;
  pin: DevicePin;
  target: L.LatLngLiteral;
  slide?: number;
}

function slideMarker(entry: DeviceMarker, to: L.LatLngLiteral) {
  if (entry.slide) cancelAnimationFrame(entry.slide);
  const from = entry.marker.getLatLng();
  const start = performance.now();
  const step = (now: number) => {
    const t = Math.min(1, (now - start) / MARKER_SLIDE_MS);
    const k = slideEase(t);
    entry.marker.setLatLng([
      from.lat + (to.lat - from.lat) * k,
      from.lng + (to.lng - from.lng) * k,
    ]);
    entry.slide = t < 1 ? requestAnimationFrame(step) : undefined;
  };
  entry.slide = requestAnimationFrame(step);
}

function stopSlide(entry: DeviceMarker) {
  if (entry.slide) cancelAnimationFrame(entry.slide);
  entry.slide = undefined;
}

function deviceIcon(pin: DevicePin): L.DivIcon {
  return L.divIcon({
    html: pin.button,
    className: "safers-poi-icon safers-device-icon",
    iconSize: [DEVICE_ICON_SIZE, DEVICE_ICON_SIZE],
    iconAnchor: [DEVICE_ICON_SIZE / 2, DEVICE_ICON_SIZE / 2],
  });
}

/**
 * 2D 지도의 장비 핀 레이어.
 * 팝업·선택 상태는 지도 컴포넌트가 들고, 여기서는 핀만 그린다.
 */
export function createDeviceMarkerLayer(
  map: L.Map,
  onSelect: (id: string, anchor: HTMLButtonElement) => void
) {
  const layer = L.layerGroup().addTo(map);
  const markers = new Map<string, DeviceMarker>();
  const labelDetail = () => String(map.getZoom() >= DEVICE_LABEL_ZOOM);
  const toggleLabels = () => {
    const detail = labelDetail();
    for (const { pin } of markers.values()) pin.button.dataset.detail = detail;
  };
  map.on("zoomend", toggleLabels);

  return {
    // 갱신된 장비 목록에 맞춰 핀을 고친다: 있던 핀은 바뀐 것만 고치고, 새 장비는 추가, 빠진 장비는 지운다.
    // 빠진 장비의 id 를 돌려준다 — 열려 있던 팝업이 그 장비면 닫는 데 쓴다.
    update(devices: ModelDevicePoint[]): string[] {
      const shown = new Set<string>();
      const detail = labelDetail();
      for (const d of devices) {
        shown.add(d.deviceId);
        let entry = markers.get(d.deviceId);
        if (!entry) {
          const pin = createDevicePin(d.deviceId, onSelect, { iconSize: DEVICE_ICON_SIZE });
          pin.button.dataset.detail = detail;
          const marker = L.marker([d.lat, d.lon], { icon: deviceIcon(pin), keyboard: false });
          marker.addTo(layer);
          entry = { marker, pin, target: { lat: d.lat, lng: d.lon } };
          markers.set(d.deviceId, entry);
        } else if (entry.target.lat !== d.lat || entry.target.lng !== d.lon) {
          entry.target = { lat: d.lat, lng: d.lon };
          slideMarker(entry, entry.target);
        }
        updateDevicePin(entry.pin, d);
      }

      const removed: string[] = [];
      for (const [id, entry] of markers) {
        if (shown.has(id)) continue;
        stopSlide(entry);
        layer.removeLayer(entry.marker);
        markers.delete(id);
        removed.push(id);
      }
      return removed;
    },
    marker(id: string) {
      const entry = markers.get(id);
      return entry && { button: entry.pin.button, latLng: entry.marker.getLatLng() };
    },
    destroy() {
      map.off("zoomend", toggleLabels);
      for (const entry of markers.values()) stopSlide(entry);
      layer.remove();
      markers.clear();
    },
  };
}

export type DeviceMarkerLayer = ReturnType<typeof createDeviceMarkerLayer>;
